import React, { FC, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import { Flex, Spin } from 'antd'
import { selectCurrentZone } from '../../store/global'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import BusinessesSummary from 'components/BusinessesSummary/BusinessesSummary'
import { BaseTableOverview, CurrentZone } from 'utils/types'
import { STREntityForTable } from 'containers/STRTablePage/types'
import { useVerticalOverviewQuery } from 'hooks/useVerticalOverviewQuery'
import { useVerticalEntitiesQuery } from 'hooks/useVerticalEntitiesQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import Search from 'UI/Search/Search'
import { keysForEmptyColumnsRequest, strColumns } from './utils'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'

const STRTablePage: FC = () => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.strIndices,
    currentQueryKey: 'str-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData, isLoading: isLoadingOverview } = useVerticalOverviewQuery<BaseTableOverview>({
    verticalIndices: currentZone?.strIndices,
    currentQueryKey: 'str-overview-key',
    searchValue: searchValueDebounced,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useVerticalEntitiesQuery<STREntityForTable>({
      verticalIndices: currentZone?.strIndices,
      currentQueryKey: 'str-entities-key',
      searchValue: searchValueDebounced,
      sorting,
      pageSize: PAGE_SIZE,
    })

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <nav id='navigationHeaderWrapper' className={styles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      <section className={styles.summary} id={'businessSummary'} ref={statisticsActivityRef}>
        <BusinessesSummary
          isLoading={isLoadingOverview}
          summary={{
            entityCounts: overviewData?.summary.entitiesCount,
            incomeCounts: overviewData?.summary.incomeCounts,
            topIndustries: overviewData?.summary.platforms.map(({ source, count }) => [source, count]),
            industriesCount: overviewData?.summary.platforms.reduce((acc, { count }) => acc + count, 0),
          }}
          isInfluencers
        />
      </section>
      {isLoading ? (
        <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
          <Spin size='large' />
        </Flex>
      ) : isError ? (
        <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
          Something went wrong
        </Alert>
      ) : (
        <>
          {emptyColumnsIsFetched && (
            <VerticalTable
              pageSize={PAGE_SIZE}
              enableColumnPicker
              tableId='str'
              dataSource={verticalEntities as any[]}
              columns={strColumns}
              containerRef={containerRef}
              additionalRefs={[statisticsActivityRef, navigationRef]}
              fetchNextPage={fetchNextPage}
              total={overviewData?.total || 0}
              loading={isFetching}
              disabledColumns={emptyColumnsData}
              onSelectionChange={setSelectedIds}
              removeFromPickerColumns={[COLUMNS_IDS.hostName]}
              rowClassName={styles.tableRow}
              onRow={record => {
                return {
                  onClick: () => {
                    window.open(`${window.location.origin}/vertical/short-term-rentals/${record.id}`, '_blank')
                  },
                }
              }}
              onChange={(pagination, filters, sorter) => {
                if (!Array.isArray(sorter)) {
                  if (sorter.order) {
                    setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                  } else {
                    setSorting([])
                  }
                }
              }}
            >
              <div className={styles.counter}>
                {`Short Term Rentals: `}
                <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
              </div>
              <Search
                placeholder='input search text'
                enterButton
                allowClear
                style={{ width: 264 }}
                loading={isFetching}
                onSearch={newSearchValue => setSearchValue(newSearchValue)}
              />
              <ExportCSVTableVertical
                zoneIndex={currentZone?.strIndices}
                csvFileName='Short-Term-Rentals'
                searchValue={searchValue}
                selectedIds={selectedIds}
                sorting={sorting}
                total={overviewData?.total}
              />
            </VerticalTable>
          )}
        </>
      )}
    </div>
  )
}

export default STRTablePage
